import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { logger } from '@magnetar/utils'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { useAuth } from '@/hooks/useAuth'
import { useViewMode } from '@/stores/viewMode'
import { adScheduleTemplateService } from '@/services/ad_schedule_template_service'
import { Loader2 } from 'lucide-react'

interface CreateTemplateDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess?: () => void
}

export default function CreateTemplateDialog({
  open,
  onOpenChange,
  onSuccess,
}: CreateTemplateDialogProps) {
  const { token } = useAuth()
  const { mode, id: viewModeId } = useViewMode()
  const [isLoading, setIsLoading] = useState(false)
  const [formData, setFormData] = useState({
    template_name: '',
    description: '',
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!token || !formData.template_name.trim()) return

    setIsLoading(true)
    try {
      // sys_admin 在 provider mode 時需要帶入 provider_id
      // provider_admin 可由 backend 自動取得
      const createData = {
        ...formData,
        ...(mode === 'provider' && viewModeId ? { provider_id: viewModeId } : {}),
      }
      const result = await adScheduleTemplateService.createTemplate(createData, token)
      if (result.success) {
        onSuccess?.()
        setFormData({ template_name: '', description: '' })
      }
    } catch (error) {
      logger.error('建立模版失敗', error)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>建立廣告排班模版</DialogTitle>
            <DialogDescription>
              建立新的廣告排班模版，之後可以編輯梯次和套用到診所
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="template_name">模版名稱 *</Label>
              <Input
                id="template_name"
                value={formData.template_name}
                onChange={(e) => setFormData({ ...formData, template_name: e.target.value })}
                placeholder="例如：標準廣告模版"
                required
                disabled={isLoading}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">描述</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="描述這個模版的用途..."
                rows={3}
                disabled={isLoading}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isLoading}
            >
              取消
            </Button>
            <Button type="submit" disabled={isLoading || !formData.template_name.trim()}>
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              建立
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
